import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';

import { Alert, Card, Spinner, useToast } from '@platform/ui';

import { IntegrationsTab } from '../../../features/tenants/integrations-tab';
import { useRefreshTenant, useTenantDetail } from '../../../features/tenants/use-tenant';
import { usePlatformClient } from '../../../lib/api/platform-client-context';

export const Route = createFileRoute('/tenants/$tenantId/integrations')({
  component: TenantIntegrationsPage,
});

/** `/tenants/$id/integrations` — per-tenant installs (FR-MKT-03, ARCHITECTURE.md §7.3). */
function TenantIntegrationsPage() {
  const { tenantId } = Route.useParams();
  const client = usePlatformClient();
  const refresh = useRefreshTenant(tenantId);
  const { toast } = useToast();
  const tenant = useTenantDetail(tenantId);
  const [error, setError] = useState<string | undefined>(undefined);

  /*
   * The detail endpoint carries only the health **summary** (four counts); the per-install
   * list is its own read, so the tenant page does not have to carry every catalogue entry
   * for every tab that does not show them (API.md §2.13).
   */
  const installs = useQuery({
    queryKey: ['platform-tenant-installs', tenantId],
    queryFn: () => client.listInstalls(tenantId),
  });

  const enable = useMutation({
    mutationFn: (key: string) => client.enableInstall(tenantId, key),
    onSuccess: async (install) => {
      setError(undefined);
      toast({
        tone: 'success',
        message: `${install.name} is allowed. The clinic admin connects the credentials.`,
      });
      await refresh();
    },
    onError: (cause: Error) => {
      setError(cause.message);
    },
  });

  const disable = useMutation({
    mutationFn: (input: { key: string; reason: string }) =>
      client.disableInstall(tenantId, input.key, { reason: input.reason }),
    onSuccess: async (install) => {
      setError(undefined);
      toast({ tone: 'success', message: `${install.name} is disabled for this clinic.` });
      await refresh();
    },
    onError: (cause: Error) => {
      setError(cause.message);
    },
  });

  if (tenant.isPending || installs.isPending) {
    return (
      <Card className="p-6">
        <Spinner label="Loading integrations" />
      </Card>
    );
  }
  if (tenant.isError || installs.isError) {
    return <Alert tone="error">That tenant could not be loaded.</Alert>;
  }

  return (
    <IntegrationsTab
      installs={installs.data}
      callLogLink={
        <Link
          to="/logs"
          search={{ tab: 'calls' as const }}
          /* M13.7: a link inside a sentence of muted text cannot be told apart by
             colour alone (WCAG 1.4.1) — 1.05:1 against the text around it. Underlined
             at rest, like every link the patient portal puts in a paragraph. */
          className="text-primary-accessible underline underline-offset-2"
        >
          Open the call log
        </Link>
      }
      error={error}
      tenantId={tenantId}
      onInstallChanged={() => {
        void refresh();
      }}
      readOnly={tenant.data.tenant.status === 'archived'}
      onEnable={(key) => {
        enable.mutate(key);
      }}
      onDisable={(key, reason) => {
        disable.mutate({ key, reason });
      }}
    />
  );
}
